import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import SearchableUserDropdown from './SearchableUserDropdown';
import { stripHtml } from '../utils/sanitize';

interface BulkCreateIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIncidentIds: string[];
  validationData?: ValidationResponse;
}

interface ValidationResponse {
  valid: boolean;
  message: string;
  autoFillData?: {
    customerId: string;
    customer: { id: string; accountName: string };
    engagementId: string;
    engagement: { id: string; name: string; number: string };
    resourceId?: string;
    resource?: { id: string; name: string };
    assignmentGroupId?: string;
    assignmentGroup?: { id: string; name: string };
    priority: string;
    description: string;
  };
  sourceCases?: Array<{
    id: string;
    number: string;
    shortDescription: string;
    priority: string;
    state: string;
  }>;
  errors?: string[];
}

const BulkCreateIncidentModal: React.FC<BulkCreateIncidentModalProps> = ({
  isOpen,
  onClose,
  selectedIncidentIds,
  validationData: propsValidationData
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [validationData, setValidationData] = useState<ValidationResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [formData, setFormData] = useState({
    shortDescription: '',
    description: '',
    priority: '',
    customerId: '',
    engagementId: '',
    servicePlanId: '',
    resourceId: '',
    assignmentGroupId: '',
    assignedTo: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [groupUsers, setGroupUsers] = useState<any[]>([]);

  // Fetch assignment groups
  const { data: groupsData } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const response = await api.get('/groups', { params: { limit: 1000 } });
      return response.data;
    },
    enabled: isOpen
  });

  // Fetch service plans for the selected engagement
  const { data: servicePlansData } = useQuery({
    queryKey: ['engagement-service-plans', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return { plans: [] };
      const response = await api.get(`/engagements/${formData.engagementId}/service-plans`);
      return response.data;
    },
    enabled: isOpen && !!formData.engagementId
  });

  // Fetch users for selected assignment group
  useEffect(() => {
    const fetchGroupUsers = async () => {
      if (formData.assignmentGroupId) {
        try {
          const response = await api.get(`/groups/${formData.assignmentGroupId}`);
          setGroupUsers(response.data.members || []);
        } catch (error) {
          console.error('Failed to fetch group users:', error);
          setGroupUsers([]);
        }
      } else {
        setGroupUsers([]);
        // Clear assigned user when group is cleared
        if (formData.assignedTo) {
          setFormData(prev => ({ ...prev, assignedTo: '' }));
        }
      }
    };

    fetchGroupUsers();
  }, [formData.assignmentGroupId]);

  // Use pre-validated data if provided, otherwise validate on open
  useEffect(() => {
    if (isOpen && selectedIncidentIds.length > 0) {
      if (propsValidationData) {
        // Use the validation data passed from parent
        setValidationData(propsValidationData);
        if (propsValidationData.valid && propsValidationData.autoFillData) {
          // Auto-fill form with validated data
          setFormData({
            shortDescription: `Consolidated incident from ${selectedIncidentIds.length} incidents`,
            description: propsValidationData.autoFillData.description,
            priority: propsValidationData.autoFillData.priority,
            customerId: propsValidationData.autoFillData.customerId,
            engagementId: propsValidationData.autoFillData.engagementId,
            servicePlanId: '',
            resourceId: propsValidationData.autoFillData.resourceId || '',
            assignmentGroupId: propsValidationData.autoFillData.assignmentGroupId || '',
            assignedTo: ''
          });
        }
      } else {
        // Fallback: validate if no data provided
        validateSelectedIncidents();
      }
    }
  }, [isOpen, selectedIncidentIds, propsValidationData]);

  const validateSelectedIncidents = async () => {
    setIsValidating(true);
    setErrors({});

    try {
      const response = await api.post('/bulk/validate-common-fields', {
        caseIds: selectedIncidentIds
      });

      setValidationData(response.data);

      if (response.data.valid && response.data.autoFillData) {
        // Auto-fill form with validated data
        setFormData({
          shortDescription: `Consolidated incident from ${selectedIncidentIds.length} incidents`,
          description: response.data.autoFillData.description,
          priority: response.data.autoFillData.priority,
          customerId: response.data.autoFillData.customerId,
          engagementId: response.data.autoFillData.engagementId,
          servicePlanId: '',
          resourceId: response.data.autoFillData.resourceId || '',
          assignmentGroupId: response.data.autoFillData.assignmentGroupId || '',
          assignedTo: ''
        });
      }
    } catch (error: any) {
      const errorData = error.response?.data;
      setValidationData({
        valid: false,
        message: errorData?.message || 'Validation failed',
        errors: errorData?.errors || ['Failed to validate selected incidents']
      });
    } finally {
      setIsValidating(false);
    }
  };

  // Create incident mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      // Get current user ID from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const callerId = user.id;

      // First create the incident via /cases endpoint with ticketType
      const incidentResponse = await api.post('/cases', {
        ticketType: 'INCIDENT',
        shortDescription: formData.shortDescription,
        description: formData.description,
        priority: formData.priority,
        customerId: formData.customerId,
        engagementId: formData.engagementId,
        servicePlanId: formData.servicePlanId || undefined,
        resourceId: formData.resourceId || undefined,
        assignedToId: formData.assignedTo || undefined,
        assignmentGroupId: formData.assignmentGroupId || undefined,
        state: 'new',
        callerId: callerId
      });

      const newIncidentId = incidentResponse.data.id;

      // Then create RELATED_TO relationships to all selected incidents
      await api.post(`/cases/${newIncidentId}/related-records`, {
        relationships: selectedIncidentIds.map(targetId => ({
          targetId,
          relationshipType: 'RELATED_TO'
        }))
      });

      return incidentResponse.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      onClose();
      // Navigate to the new incident
      navigate(`/incidents/${data.id}`);
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.message || 'Failed to create incident';
      setErrors({ submit: errorMsg });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = 'Short description is required';
    }
    if (!formData.assignedTo) {
      newErrors.assignedTo = 'Assigned to is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    createMutation.mutate();
  };

  const handleClose = () => {
    setFormData({
      shortDescription: '',
      description: '',
      priority: '',
      customerId: '',
      engagementId: '',
      servicePlanId: '',
      resourceId: '',
      assignmentGroupId: '',
      assignedTo: ''
    });
    setValidationData(null);
    setErrors({});
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Create Consolidated Incident from {selectedIncidentIds.length} Selected Incidents
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="px-6 py-4">
                  {/* Validation Loading */}
                  {isValidating && (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      <p className="mt-2 text-sm text-gray-500">Validating selected incidents...</p>
                    </div>
                  )}

                  {/* Validation Errors */}
                  {!isValidating && validationData && !validationData.valid && (
                    <div className="rounded-md bg-red-50 border border-red-200 p-4 mb-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">Validation Failed</h3>
                          <div className="mt-2 text-sm text-red-700">
                            <p>{validationData.message}</p>
                            {validationData.errors && validationData.errors.length > 0 && (
                              <ul className="list-disc list-inside mt-2 space-y-1">
                                {validationData.errors.map((error, index) => (
                                  <li key={index}>{error}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Form */}
                  {!isValidating && validationData && validationData.valid && (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Source Incidents Info */}
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                        <h4 className="text-sm font-medium text-blue-900 mb-2">Source Incidents</h4>
                        <div className="text-sm text-blue-800 space-y-1">
                          {validationData.sourceCases?.slice(0, 5).map((incident) => (
                            <div key={incident.id} className="flex items-center space-x-2">
                              <span className="font-medium">{incident.number}</span>
                              <span>-</span>
                              <span className="truncate">{stripHtml(incident.shortDescription)}</span>
                            </div>
                          ))}
                          {validationData.sourceCases && validationData.sourceCases.length > 5 && (
                            <div className="text-xs text-blue-600">
                              ...and {validationData.sourceCases.length - 5} more
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Form Fields */}
                      <div className="space-y-4">
                        {/* Row 1: Customer and Engagement */}
                        <div className="grid grid-cols-2 gap-4">
                          {/* Customer */}
                          <div className="flex items-start gap-3">
                            <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                              Customer
                            </label>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={validationData.autoFillData?.customer.accountName || ''}
                                disabled
                                className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                              />
                            </div>
                          </div>

                          {/* Engagement */}
                          <div className="flex items-start gap-3">
                            <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                              Engagement
                            </label>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={validationData.autoFillData?.engagement.name || ''}
                                disabled
                                className="block w-full rounded-md border border-gray-300 bg-gray-100 shadow-sm py-2 px-3 sm:text-sm cursor-not-allowed"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Row 2: Service Plan and Priority */}
                        <div className="grid grid-cols-2 gap-4">
                          {/* Service Plan */}
                          <div className="flex items-start gap-3">
                            <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                              Service Plan
                            </label>
                            <div className="flex-1">
                              <select
                                value={formData.servicePlanId}
                                onChange={(e) => setFormData({ ...formData, servicePlanId: e.target.value })}
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              >
                                <option value="">Select Service Plan</option>
                                {servicePlansData?.plans?.map((plan: any) => (
                                  <option key={plan.id} value={plan.id}>
                                    {plan.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Priority */}
                          <div className="flex items-start gap-3">
                            <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                              Priority
                            </label>
                            <div className="flex-1">
                              <select
                                value={formData.priority}
                                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              >
                                <option value="critical_1">Critical</option>
                                <option value="high_2">High</option>
                                <option value="moderate_3">Moderate</option>
                                <option value="low_4">Low</option>
                                <option value="planning_5">Planning</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Row 3: Short Description */}
                        <div className="flex items-start gap-3">
                          <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                            Short Description <span className="text-red-500">*</span>
                          </label>
                          <div className="flex-1">
                            <input
                              type="text"
                              value={formData.shortDescription}
                              onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                              className={`block w-full rounded-md border bg-white shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                errors.shortDescription ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="Brief description of the incident"
                            />
                            {errors.shortDescription && (
                              <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
                            )}
                          </div>
                        </div>

                        {/* Row 4: Description */}
                        <div className="flex items-start gap-3">
                          <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                            Description
                          </label>
                          <div className="flex-1">
                            <textarea
                              value={formData.description}
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                              rows={6}
                              className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              placeholder="Auto-generated from selected incidents"
                            />
                          </div>
                        </div>

                        {/* Row 5: Assignment Group and Assigned To */}
                        <div className="grid grid-cols-2 gap-4">
                          {/* Assignment Group */}
                          <div className="flex items-start gap-3">
                            <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                              Assignment Group
                            </label>
                            <div className="flex-1">
                              <select
                                value={formData.assignmentGroupId}
                                onChange={(e) => setFormData({ ...formData, assignmentGroupId: e.target.value })}
                                className="block w-full rounded-md border border-gray-300 bg-white shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              >
                                <option value="">Select Assignment Group</option>
                                {groupsData?.groups?.map((group: any) => (
                                  <option key={group.id} value={group.id}>
                                    {group.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Assigned To */}
                          <div className="flex items-start gap-3">
                            <label className="w-32 flex-shrink-0 text-sm font-medium text-gray-700 pt-2 text-right">
                              Assigned To
                            </label>
                            <div className="flex-1">
                              <SearchableUserDropdown
                                value={formData.assignedTo}
                                onChange={(userId) => setFormData({ ...formData, assignedTo: userId || '' })}
                                placeholder="Select user to assign..."
                                disabled={!formData.assignmentGroupId}
                                filterUserIds={groupUsers.map((u: any) => u.userId || u.id)}
                              />
                              {errors.assignedTo && (
                                <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Submit Error */}
                      {errors.submit && (
                        <div className="rounded-md bg-red-50 p-4">
                          <div className="text-sm text-red-700">{errors.submit}</div>
                        </div>
                      )}

                      {/* Info Message */}
                      <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                              Related Records
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700">
                              <p>
                                This new incident will be created and linked to all selected incidents.
                                Each incident will follow its own independent workflow.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={createMutation.isPending}
                          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
                        >
                          {createMutation.isPending ? 'Creating...' : 'Create Incident'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BulkCreateIncidentModal;
